"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import { RefreshCw } from "lucide-react"
import { useApiClient } from "@/api/useApiClient"
import { useI18n, formatEurFromCents } from "@/i18n"
import { Button } from "@/components/ui/button"
import { InfoButton } from "@/components/ui/InfoButton"

/**
 * S-2 fix: GET /v1/plans/current now carries `latestJob` (the newest
 * plan_jobs row, any status) — when it's failed, the CURRENT plan shown is
 * still the last successfully-applied one, but the user has no way to know
 * their last regeneration attempt silently failed. Same warning-banner
 * styling as PlanWarningsBanner.tsx (amber-300/amber-50/amber-900).
 *
 * "regeneration" is an existing PlanJobCreateRequestSchema trigger value
 * (packages/contracts/src/schemas/planJobs.ts) — reused rather than adding
 * a new one, same idiom as PlanPage.tsx's existing recovery path polling
 * getCurrentPlan after createPlanJob({ trigger: "manual" }).
 */
import type { PlanLatestJob } from "@bitebalance/contracts"
import { Link } from "react-router-dom"
import { BUDGET_SETTINGS_PATH } from "@/lib/budgetMigration"

export function RegenFailedBanner({
  onRegenerated,
  failedError,
  applicability,
}: {
  onRegenerated: () => void
  /** 2026-09-02 finding B: the job row's authoritative error — a
   * deterministic BUDGET_INFEASIBLE renders its real numbers and real
   * choices instead of a generic "try again" that cannot succeed. */
  failedError?: PlanLatestJob["failedError"]
  /** Owner invariant 2026-09-04: the SERVER-derived applicability of a
   * failed job to the user's CURRENT settings. Drives the whole
   * BUDGET_INFEASIBLE branch — `current` renders the maximum failure,
   * `superseded_by_settings` (or an unresolved/absent block) renders the
   * calm "built with earlier settings" notice. Never inferred client-side
   * from `failedError`. */
  applicability?: PlanLatestJob["applicability"]
}) {
  const { t, locale } = useI18n()
  const api = useApiClient()
  const [regenerating, setRegenerating] = useState(false)

  async function handleRegenerate() {
    setRegenerating(true)
    try {
      const job = await api.createPlanJob({ trigger: "regeneration" })
      for (let attempt = 0; attempt < 15; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 2000))
        try {
          const jobRes = await api.getPlanJob(job.id)
          if (jobRes.status === "completed" || jobRes.status === "failed") break
        } catch {
          // keep polling — a transient read failure shouldn't abandon the wait
        }
      }
    } finally {
      setRegenerating(false)
      onRegenerated()
    }
  }

  const money = (cents: number) => formatEurFromCents(cents, locale)

  if (failedError?.code === "BUDGET_INFEASIBLE") {
    // A persisted budget failure is a CURRENT condition only while the
    // server says its constraint still matches the user's settings. Any
    // other state — superseded, or an applicability block we cannot read —
    // is history: explain that the plan was built under earlier settings
    // and offer the rebuild. Never "your maximum", never the attempt's
    // amount as a current constraint (owner invariant 1).
    if (applicability?.status !== "current") {
      return (
        <div
          role="status"
          data-testid="plan-stale-settings"
          className="flex gap-3 rounded-bb-lg border border-gold/50 bg-gold/10 px-4 py-3 text-sm text-ink"
        >
          <RefreshCw className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
          <div>
            <p className="font-semibold">{t("views.budget.staleSettingsTitle")}</p>
            <p className="mt-1 text-ink-2">{t("views.budget.staleSettingsBody")}</p>
            <Button
              type="button"
              size="sm"
              className="mt-2.5 min-h-11"
              disabled={regenerating}
              onClick={() => void handleRegenerate()}
            >
              {t("views.budget.staleSettingsCta")}
            </Button>
          </div>
        </div>
      )
    }

    // Budget Model v2 (ADR-052 §7.1): ONLY the hard maximum checkout can
    // fail a rebuild (the spending goal is soft and never does). Retrying
    // the same inputs gives the same result — say so, show the
    // authoritative numbers (maximum, cheapest valid checkout, exact gap),
    // and point to Settings where the maximum can be changed or removed.
    // No retry button, no override. A `legacy_limit` source (an
    // unclassified pre-v2 weekly budget) gets the plain "your previous
    // budget" wording; the CTA lands on the budget SETUP card in Settings
    // (#grocery-budget) — never internal terms.
    const details = (failedError.details ?? {}) as {
      maxSource?: "maximum_checkout" | "legacy_limit" | null
      maxCheckoutCents?: number
      cheapestValidCheckoutCents?: number
      checkoutDeltaCents?: number
      /** pre-v2 names (mirrored by the server for one release) */
      budgetCents?: number
      cheapestValidCostCents?: number
      gapCents?: number
    }
    const unclassifiedLimit =
      applicability.constraintSource === "legacy_limit" ||
      applicability.currentCeilingSource === "legacy_limit" ||
      details.maxSource === "legacy_limit"
    const maxCents =
      applicability.currentCeilingCents ??
      applicability.attemptCeilingCents ??
      details.maxCheckoutCents ??
      details.budgetCents ??
      0
    return (
      <div
        role="alert"
        data-testid="plan-max-infeasible"
        className="flex gap-3 rounded-bb-lg border border-gold/50 bg-gold/10 px-4 py-3 text-sm text-ink"
      >
        <RefreshCw className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
        <div>
          <p className="flex items-center gap-2 font-semibold">
            <span>{t("views.budget.planMaxInfeasibleTitle")}</span>
            <InfoButton termId="maximum-checkout" />
          </p>
          <p className="mt-1 text-ink-2">
            {t(unclassifiedLimit ? "views.budget.planLegacyInfeasibleBody" : "views.budget.planMaxInfeasibleBody", {
              max: money(maxCents),
              cheapest: money(details.cheapestValidCheckoutCents ?? details.cheapestValidCostCents ?? 0),
              over: money(details.checkoutDeltaCents ?? details.gapCents ?? 0),
            })}
          </p>
          {/* Owner invariant 4: the cheapest candidate of a FAILED rebuild
              was never materialized — it is not the current shopping list. */}
          <p className="mt-1 flex items-center gap-1 text-xs text-ink-2" data-testid="plan-candidate-note">
            <span>{t("views.budget.candidateNote")}</span>
            <InfoButton termId="cheapest-valid-week" />
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-3">
            <Button asChild size="sm" className="min-h-11">
              <Link to={BUDGET_SETTINGS_PATH}>{t("views.budget.planMaxInfeasibleCta")}</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      role="alert"
      className="flex gap-3 rounded-bb-lg border border-gold/50 bg-gold/10 px-4 py-3 text-sm text-ink"
    >
      <RefreshCw className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden="true" />
      <div>
        <p className="font-semibold">{t("views.plan.regenFailedTitle")}</p>
        <p className="mt-1 text-ink-2">{t("views.plan.regenFailedBody")}</p>
        <Button
          type="button"
          size="sm"
          className="mt-2.5 min-h-11"
          disabled={regenerating}
          onClick={() => void handleRegenerate()}
        >
          {t("views.plan.regenCta")}
        </Button>
      </div>
    </div>
  )
}
